import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { usePageTitle } from '@/hooks/usePageTitle';
import { aboutInfo } from '@/mocks/movies';

export default function About() {
  const location = useLocation();
  usePageTitle('关于我们 - 糖心影视 | 品牌介绍与联系方式');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <section className="relative w-full h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20cinematic%20background%20with%20soft%20gradients%20of%20coral%20and%20amber%2C%20elegant%20flowing%20shapes%20like%20theater%20curtains%2C%20subtle%20film%20texture%2C%20minimalist%20and%20artistic%20style%2C%20warm%20atmosphere&width=1920&height=600&seq=about-hero-2026&orientation=landscape"
              alt="关于糖心影视"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-3">关于糖心影视</h1>
            <p className="text-background-50/70 text-sm md:text-base max-w-lg mx-auto">{aboutInfo.slogan}</p>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-12">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center p-5 rounded-xl bg-background-100 border border-background-200">
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="max-w-3xl mx-auto">
              <div className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">品牌介绍</h2>
                <div className="space-y-4 text-foreground-700 leading-relaxed">
                  <p className="text-base">{aboutInfo.description}</p>
                  <p className="text-base">平台自上线以来，始终坚持<strong>高清流畅、即点即播</strong>的服务理念，通过智能推荐算法和精细化内容运营，为用户打造个性化的观影体验。我们与国内外多家影视版权方建立了深度合作关系，确保平台内容的丰富性和时效性。</p>
                  <p className="text-base">糖心影视的核心团队来自互联网和影视行业的资深从业者，在内容分发、视频技术和用户体验方面拥有丰富的实践经验。我们不断优化播放技术，支持多端同步观看，让用户随时随地享受高品质的影视内容。</p>
                </div>
              </div>

              <div className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">使命与愿景</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-5 rounded-xl bg-primary-50 border border-primary-100">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                      <i className="ri-flag-line text-xl text-primary-500"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">我们的使命</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.mission}</p>
                  </div>
                  <div className="p-5 rounded-xl bg-accent-50 border border-accent-100">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 mb-3">
                      <i className="ri-eye-line text-xl text-accent-600"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">我们的愿景</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.vision}</p>
                  </div>
                </div>
              </div>

              <div className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">用户口碑</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { name: '影迷小王', avatar: '影', comment: '糖心影视是我用过最流畅的观影平台，片源更新也很快，追剧必备！', rating: 5 },
                    { name: '追剧达人Linda', avatar: '达', comment: '画质清晰，分类齐全，每天下班回家打开糖心影视已经成了习惯。', rating: 5 },
                    { name: '电影爱好者老张', avatar: '张', comment: '片库真的很全，好多小众电影都能找到，推荐给了身边所有朋友。', rating: 5 },
                    { name: '学生党小陈', avatar: '陈', comment: '完全免费还没有广告，对穷学生太友好了，良心平台！', rating: 5 },
                    { name: '宝妈悠悠', avatar: '悠', comment: '动漫资源特别丰富，孩子喜欢的动画片都有，画质也很棒。', rating: 4 },
                    { name: '程序员阿杰', avatar: '杰', comment: '界面简洁不花哨，加载速度快，技术体验做得很好。', rating: 5 },
                  ].map((review) => (
                    <div key={review.name} className="p-5 rounded-xl bg-background-100 border border-background-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-heading text-sm font-semibold">
                          {review.avatar}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: review.rating }).map((_, i) => (
                              <div key={i} className="w-3.5 h-3.5 flex items-center justify-center text-accent-500">
                                <i className="ri-star-fill text-xs"></i>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div id="contact">
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">联系我们</h2>
                <div className="p-6 rounded-xl bg-background-100 border border-background-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                        <i className="ri-mail-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-sm text-foreground-400 mb-0.5">电子邮箱</p>
                        <p className="text-foreground-800 font-medium">{aboutInfo.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                        <i className="ri-phone-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-sm text-foreground-400 mb-0.5">客服电话</p>
                        <p className="text-foreground-800 font-medium">{aboutInfo.contact.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-sm text-foreground-400 mb-0.5">公司地址</p>
                        <p className="text-foreground-800 font-medium">{aboutInfo.contact.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                        <i className="ri-wechat-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-sm text-foreground-400 mb-0.5">微信公众号</p>
                        <p className="text-foreground-800 font-medium">{aboutInfo.contact.wechat}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "AboutPage",
              "name": "关于糖心影视",
              "description": aboutInfo.description,
              "url": "https://www.tangxinys.com/about",
              "publisher": {
                "@type": "Organization",
                "name": "糖心影视",
                "url": "https://www.tangxinys.com",
                "contactPoint": {
                  "@type": "ContactPoint",
                  "telephone": aboutInfo.contact.phone,
                  "contactType": "customer service",
                  "email": aboutInfo.contact.email
                },
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "望京SOHO T3 18层",
                  "addressLocality": "北京",
                  "addressRegion": "朝阳区",
                  "addressCountry": "CN"
                }
              }
            })
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "糖心影视需要付费吗？",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "糖心影视平台完全免费，所有影视资源均可在线观看，无需付费或订阅。"
                  }
                },
                {
                  "@type": "Question",
                  "name": "糖心影视支持哪些设备观看？",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "糖心影视支持电脑、平板、手机等多种设备在线观看，只需打开浏览器即可享受高清观影体验。"
                  }
                },
                {
                  "@type": "Question",
                  "name": "糖心影视的视频画质如何？",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "糖心影视提供高清画质播放，支持1080P超清画质，确保观影体验流畅清晰。"
                  }
                },
                {
                  "@type": "Question",
                  "name": "糖心影视的资源更新频率是多久？",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "糖心影视每日更新最新影视资源，热门电影、电视剧、综艺、动漫均会及时同步更新。"
                  }
                }
              ]
            })
          }}
        />
      </main>
      <Footer />
    </>
  );
}